/**
 * UI state: theme, sound, toasts, dialogs, screen-reader announcements,
 * onboarding coach-marks and celebration events.
 */
import { create } from 'zustand';
import { KEYS, readString, writeString } from '../lib/storage';
import { randomToken } from '../lib/ids';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
  /** Optional call to action, e.g. Reload. */
  action?: { label: string; run: () => void };
  /** ms before auto-dismiss; 0 = sticky. */
  duration: number;
}

export type DialogId = 'howto' | 'shortcuts' | 'exchange' | 'pass' | 'resign' | 'leave' | 'history' | 'menu' | 'preview';

/** The transient pill above the board ("Your turn", "Game on! Alex goes first"). */
export interface Banner {
  id: string;
  kind: 'turn' | 'start';
  title: string;
  detail?: string;
}

export interface Announcement {
  id: string;
  text: string;
  politeness: 'polite' | 'assertive';
  /** Local time it was made (announcements made together are merged). */
  at: number;
}

/** Announcements this close together are merged into one utterance. */
export const ANNOUNCE_MERGE_MS = 300;

/** A celebration request consumed by <Celebration/>. */
export interface CelebrationEvent {
  id: string;
  kind: 'score' | 'good' | 'big' | 'bingo' | 'win';
  score: number;
  /** Seat that scored (the float flies into that player's card). */
  seat: 0 | 1;
  /** Board squares of the scoring word (float origin). */
  cells: { row: number; col: number }[];
}

export const ONBOARDING_STEPS = 3;

function readTheme(): ThemePreference {
  const v = readString(KEYS.theme);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

export function resolveTheme(pref: ThemePreference, systemDark: boolean): ResolvedTheme {
  if (pref === 'system') return systemDark ? 'dark' : 'light';
  return pref;
}

/** Apply a resolved theme to <html> (data-theme + color-scheme + theme-color meta). */
export function applyTheme(theme: ResolvedTheme): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.style.colorScheme = theme;
  const color = theme === 'dark' ? '#10131A' : '#F3EEE4';
  document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute('content', color));
}

export interface UiState {
  theme: ThemePreference;
  resolvedTheme: ResolvedTheme;
  sound: boolean;
  toasts: Toast[];
  dialog: DialogId | null;
  announcement: Announcement | null;
  /** Index of the visible coach-mark, or null when onboarding is done/dismissed. */
  onboardingStep: number | null;
  celebrations: CelebrationEvent[];
  gameOverDismissed: boolean;
  boardZoomed: boolean;
  banner: Banner | null;

  setTheme(pref: ThemePreference): void;
  syncSystemTheme(): void;
  setSound(on: boolean): void;
  /**
   * Show a toast. It is also announced to screen readers (errors assertively)
   * unless `announce: false` — the toast stack itself is not a live region.
   */
  toast(
    message: string,
    opts?: { kind?: ToastKind; duration?: number; action?: Toast['action']; id?: string; announce?: boolean },
  ): string;
  dismissToast(id: string): void;
  openDialog(id: DialogId): void;
  closeDialog(): void;
  announce(text: string, politeness?: Announcement['politeness']): void;
  startOnboarding(): void;
  advanceOnboarding(): void;
  dismissOnboarding(): void;
  celebrate(e: Omit<CelebrationEvent, 'id'>): void;
  consumeCelebration(id: string): void;
  setGameOverDismissed(v: boolean): void;
  setBoardZoomed(v: boolean): void;
  showBanner(b: Omit<Banner, 'id'>): void;
  hideBanner(id: string): void;
}

export const useUiStore = create<UiState>()((set, get) => {
  const theme = readTheme();
  return {
    theme,
    resolvedTheme: resolveTheme(theme, systemPrefersDark()),
    sound: readString(KEYS.sound) !== 'off',
    toasts: [],
    dialog: null,
    announcement: null,
    onboardingStep: null,
    celebrations: [],
    gameOverDismissed: false,
    boardZoomed: false,
    banner: null,

    setTheme(pref) {
      writeString(KEYS.theme, pref);
      const resolved = resolveTheme(pref, systemPrefersDark());
      applyTheme(resolved);
      set({ theme: pref, resolvedTheme: resolved });
    },

    syncSystemTheme() {
      const resolved = resolveTheme(get().theme, systemPrefersDark());
      if (resolved !== get().resolvedTheme) {
        applyTheme(resolved);
        set({ resolvedTheme: resolved });
      }
    },

    setSound(on) {
      writeString(KEYS.sound, on ? 'on' : 'off');
      set({ sound: on });
    },

    toast(message, opts = {}) {
      const id = opts.id ?? randomToken(8);
      const kind = opts.kind ?? 'info';
      const toast: Toast = {
        id,
        kind,
        message,
        duration: opts.duration ?? (kind === 'error' ? 5200 : 3400),
      };
      if (opts.action) toast.action = opts.action;
      // Same id replaces (e.g. a single "new version" toast); cap the stack at 4.
      set((s) => ({ toasts: [...s.toasts.filter((t) => t.id !== id), toast].slice(-4) }));
      if (opts.announce !== false) get().announce(message, kind === 'error' ? 'assertive' : 'polite');
      return id;
    },

    dismissToast(id) {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    },

    openDialog(id) {
      set({ dialog: id });
    },

    closeDialog() {
      set({ dialog: null });
    },

    announce(text, politeness = 'polite') {
      const now = Date.now();
      const prev = get().announcement;
      // Several things often happen in one view update ("Alex played QUIRK for
      // 38." + "Your turn."): merge them so the second doesn't cut off the first.
      if (prev && now - prev.at < ANNOUNCE_MERGE_MS) {
        // Already said (e.g. a toast repeating the announcement): nothing to add.
        if (prev.text.includes(text)) return;
        const merged = prev.politeness === 'assertive' || politeness === 'assertive' ? 'assertive' : 'polite';
        set({ announcement: { id: randomToken(6), text: `${prev.text} ${text}`, politeness: merged, at: now } });
        return;
      }
      set({ announcement: { id: randomToken(6), text, politeness, at: now } });
    },

    startOnboarding() {
      if (readString(KEYS.hintsDismissed) === '1') return;
      if (get().onboardingStep === null) set({ onboardingStep: 0 });
    },

    advanceOnboarding() {
      const step = get().onboardingStep;
      if (step === null) return;
      if (step + 1 >= ONBOARDING_STEPS) get().dismissOnboarding();
      else set({ onboardingStep: step + 1 });
    },

    dismissOnboarding() {
      writeString(KEYS.hintsDismissed, '1');
      set({ onboardingStep: null });
    },

    celebrate(e) {
      set((s) => ({ celebrations: [...s.celebrations, { ...e, id: randomToken(8) }] }));
    },

    consumeCelebration(id) {
      set((s) => ({ celebrations: s.celebrations.filter((c) => c.id !== id) }));
    },

    setGameOverDismissed(v) {
      set({ gameOverDismissed: v });
    },

    setBoardZoomed(v) {
      set({ boardZoomed: v });
    },

    showBanner(b) {
      set({ banner: { ...b, id: randomToken(6) } });
    },

    hideBanner(id) {
      if (get().banner?.id === id) set({ banner: null });
    },
  };
});
